import { ConversionService } from 'app/_directives/conversionService';
import { Growl } from 'angular/custom/services/growl.service';
import * as angular from 'angular';
import { Decimal } from 'decimal.js';
import { Debounce } from '../_services/debounce.factory';
import { FeatureTogglesResource } from 'angular/resources/feature-toggles.resource';

(function () {
    'use strict';

    /**
     * @ngdoc directive
     * @name pkpStaBid
     * @memberof app
     *
     * @restrict E
     * @scope
     *
     * @description
     * Form for short term auction bidding with auction details appended.
     *
     * @param {Object?} auction When no lazy loading should be done, the auction object will be available right away.
     * @param {Number?} auctionId Used for lazy loading, to load the auction for the id.
     * @param {Number?} bidId Requests the bid from the backend to allow editing of a bid.
     * @param {boolean?} lazyLoad When specified, the `auctionId` will be used to request the auction from
     *  backend service.
     * @param {expression} onClose Expression evaluated when the user closes the foldout.
     * @param {function} onBidSuccess Function called when a short term bid was successfully submitted.
     *
     * @author flzemke.btc
     */
    angular.module('app').directive('pkpStaBid', pkpStaBid);

    function pkpStaBid(
        $q,
        $parse,
        auctionsOverviewService,
        auctionResource,
        conversionService: ConversionService,
        INCLUDE,
        growl: Growl,
        debounce: Debounce,
        featureTogglesResource: FeatureTogglesResource
    ) {
        return {
            restrict: 'E',
            scope: {
                auction: '=?',
                auctionId: '=?',
                bidId: '=?',
                uiPlace: '=?',
                lazyLoad: '=?',
                onUndiscountedChange: '&',
                onBidSuccess: '&'
            },
            template: require('app/_directives/pkp-sta-bid.directive.html'),
            link: linkFn
        };

        function linkFn(scope, elem, attr) {
            scope.INCLUDE = INCLUDE;
            scope.MINIMUM_BID_INFO_BLAH =
                auctionsOverviewService.MINIMUM_BID_INFO_BLAH;
            scope.uiLoadingAuction = true;
            scope.uiCompetitionsLoading = true;
            scope.uiLoadingErrorAuction = false;
            scope.uiSubmitting = false;
            scope.config = {};
            scope.bidFormWrapper = {};
            scope.bid = {};
            scope.isUpgradePossible = false;
            scope.isUnbundledToBundledConversionPossible = false;
            scope.ACTION_TYPE = auctionsOverviewService.ACTION_TYPE;
            scope.submitShortTermBid = submitShortTermBid;
            scope.toggleUpgrade = toggleUpgrade;
            scope.toggleUnbundledToBundledConversion =
                toggleUnbundledToBundledConversion;
            scope.close = close;
            scope.findMaxQuantity = findMaxQuantity;
            scope.onConversionAmountChange = onConversionAmountChange;
            scope.showUpgradeSettings = { boundVal: false };
            scope.showUnbundledToBundledConversionSettings = {
                boundVal: false
            };
            scope.featureFlags = {
                useEightFractionalDigitsUPABidCheck: false
            };

            featureTogglesResource.getUrls().subscribe(({ flags }) => {
                scope.featureFlags = {
                    useEightFractionalDigitsUPABidCheck:
                        !!flags.useEightFractionalDigitsUPABidCheck
                };
            });

            const validateMaxQuantityDebounced: () => angular.IPromise<any> =
                debounce(
                    () =>
                        scope.bidFormWrapper.bidForm.maxQuantity != null &&
                        scope.bidFormWrapper.bidForm.maxQuantity.$validate(),
                    500
                );

            var configPromise = auctionResource.config(
                { auctionId: scope.auctionId },
                function (res) {
                    scope.config = res;
                }
            ).$promise;

            var auctionPromise = null;
            var bidPromise = null;

            if (scope.lazyLoad === true) {
                auctionPromise = auctionResource.get(
                    { auctionId: scope.auctionId },
                    function (res) {
                        scope.auction = res;
                    }
                ).$promise;
            } else {
                scope.lazyLoad = false;
                scope.auctionId = scope.auction.id;
            }

            if (!angular.isUndefined(scope.bidId)) {
                bidPromise = auctionResource.orders(
                    { auctionId: scope.auctionId },
                    function (res) {
                        scope.bid = auctionsOverviewService.findUnifiedBidsById(
                            res,
                            scope.bidId,
                            true
                        );
                    }
                ).$promise;
            }

            $q.all([configPromise, auctionPromise, bidPromise])
                .catch(function () {
                    scope.uiLoadingErrorAuction = true;
                })
                .finally(function () {
                    scope.isUpgradePossible =
                        conversionService.isUpgradePossible(
                            scope.config,
                            scope.auction.capacityCategory0,
                            scope.auction.capacityCategory1
                        );
                    scope.showUpgradeSettings.boundVal = !!(
                        scope.bid.upgrade0 || scope.bid.upgrade1
                    );

                    scope.isUnbundledToBundledConversionPossible =
                        conversionService.isConversionPossible(
                            scope.config,
                            scope.auction.networkPoint.bundled
                        );

                    scope.showUnbundledToBundledConversionSettings.boundVal =
                        !!(
                            (scope.bid.conversions &&
                                scope.bid.conversions[0] &&
                                scope.bid.conversions[0].conversion0) ||
                            (scope.bid.conversions &&
                                scope.bid.conversions[0] &&
                                scope.bid.conversions[0].conversion1)
                        );

                    scope.nonUndiscountedTariffsSupported =
                        scope.auction.networkPoint.connectionPointType ===
                            'RESERVOIR' &&
                        scope.auction.networkPoint.undiscountedTariffs;

                    scope.uiLoadingAuction = false;
                });

            function findMaxQuantity(): number {
                const maxQuantityOfConversions =
                    conversionService.findMaxQuantityOfConversions(
                        scope.bid.conversion0 != null &&
                            scope.config.limitBidMandatoryForConversion0
                            ? scope.bid.conversion0.intervals
                            : null,
                        scope.bid.conversion1 != null &&
                            scope.config.limitBidMandatoryForConversion1
                            ? scope.bid.conversion1.intervals
                            : null
                    );

                if (
                    maxQuantityOfConversions != null &&
                    maxQuantityOfConversions.lessThan(
                        new Decimal(
                            scope.auction.marketable.originalValue.value
                        )
                    )
                ) {
                    scope.maxQuantityValidationMessage =
                        'Please enter any bid amount equal to the amount requested for conversion. If you want to ' +
                        'acquire more capacity, please refrain from requesting a conversion or add another bid without placing a conversion request.';
                    return maxQuantityOfConversions.toNumber();
                } else {
                    scope.maxQuantityValidationMessage =
                        'Quantity exceeds marketable capacity.';
                    return scope.auction.marketable.originalValue.value;
                }
            }

            function onConversionAmountChange() {
                validateMaxQuantityDebounced().then(angular.noop);
            }

            /**
             * Submit the short term auction and post to endpoint.
             *
             * @param {boolean} isValid This is the Angular form validation attribute <tt>scope.bidForm.$valid</tt>.
             * @returns {boolean} true if a call was made to the server, false otherwise.
             */
            function submitShortTermBid(isValid) {
                if (!isValid) {
                    return false;
                } else if (
                    !conversionService.validateConversionAndUpgradeAreNotBothActiveForTheSameNetworkPoint(
                        scope.bid.conversions && scope.bid.conversions[0]
                            ? scope.bid.conversions[0].conversion0
                            : undefined,
                        scope.bid.conversions && scope.bid.conversions[0]
                            ? scope.bid.conversions[0].conversion1
                            : undefined,
                        scope.bid.upgrade0,
                        scope.bid.upgrade1
                    )
                ) {
                    growl.show(
                        'It is not possible to upgrade and convert capacity at the same network point.',
                        'danger'
                    );
                    return false;
                }

                if (!scope.uiCheckPage) {
                    scope.uiCheckPage = true;
                    return false;
                }

                scope.uiSubmitting = true;
                scope.uiFormPostedToService = false;

                auctionResource
                    .bidShortTerm(
                        { auctionId: scope.auction.id },
                        scope.bid,
                        function () {
                            scope.uiSubmitSuccess = true;
                            scope.uiShowCloseButton = true;
                            scope.onBidSuccess();
                        },
                        function (err) {
                            scope.uiSubmitting = false;
                            scope.uiSubmitSuccess = false;
                            const errorMessage =
                                err && err.data && err.data.message
                                    ? err.data.message
                                    : 'An unknown error occurred. Please, check back later.';
                            scope.uiSubmitErrorMsg = errorMessage;
                        }
                    )
                    .$promise.finally(function () {
                        scope.uiFormPostedToService = true;
                    });

                return true;
            }

            function toggleUpgrade() {
                delete scope.bid.upgrade0;
                delete scope.bid.upgrade1;
            }

            function toggleUnbundledToBundledConversion() {
                if (scope.bid.conversions && scope.bid.conversions[0]) {
                    delete scope.bid.conversions[0].conversion0;
                    delete scope.bid.conversions[0].conversion1;
                }
            }

            /**
             * Parse <tt>onClose</tt> attribute attached to this directive and executes it.
             * Kinda lik ngClick, but for the custom close event of this directive.
             */
            function close() {
                $parse(attr.onClose)(scope.$parent);
            }
        }
    }
})();
